import { CloseSquareOutlined, UnorderedListOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Spin,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import Notiflix from "notiflix";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import "../../../assets/styles/DatePicker.css";
import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import { rightAction } from "../../../redux/actions";
import cnt from "../../../services/definedConstant";
import { BOM } from "../../../services/definedMethod";
import { apiConfig, labelConfig, staticConfig } from "../teller.config";
import CurrencyDenominationInfo from "./components/CurrencyDenominationInfo";
import CurrencySummary from "./components/CurrencySummary";
import { showCheckerApprovalModal } from "../../../components/CheckerModal";
import { useNavigate } from "react-router-dom";

const { Text, Title } = Typography;

const getBatchStatusCode = (status) =>
  String(status ?? "")
    .trim()
    .charAt(0)
    .toUpperCase();

const isSameAsToday = (date) => {
  const parsedDate = dayjs(date);

  return parsedDate.isValid() && parsedDate.isSame(dayjs(), "day");
};

function TellerBatch(props) {
  const { userData } = props;
  const { session } = userData;
  const [form] = Form.useForm();
  const [tellerStatus, setTellerStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [denominationEntries, setDenominationEntries] = useState([]);
  const [summaryEntries, setSummaryEntries] = useState([]);
  const [template, setTemplate] = useState([]);
  const [currencyDenominationOptions, setCurrencyDenominationOptions] =
    useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [currencyCodes, setCurrencyCodes] = useState([]);
  const [batchUser, setBatchUser] = useState("");
  const [balanceData, setBalanceData] = useState([]);
  const [transactionsData, setTransactionsData] = useState([]);
  const [allowDenomTracking, setAllowDenomTracking] = useState(false);
  const navigate = useNavigate();

  const balanceColumns = [
    { title: "Currency", dataIndex: "currency", key: "currency" },
    { title: "Balance", dataIndex: "balance", key: "balance" },
  ];

  const denominationColumns = [
    { title: "Currency", dataIndex: "currency", key: "currency" },
    {
      title: "Denomination Code",
      dataIndex: "denominationCode",
      key: "denominationCode",
    },
    {
      title: "Denomination Count",
      dataIndex: "denominationCount",
      key: "denominationCount",
    },
    {
      title: "Denomination Value",
      dataIndex: "denominationValue",
      key: "denominationValue",
      render: (value) => BOM.FormatAmount(value),
    },
  ];

  const denominationData = currencyOptions?.map((currency) => ({
    currency: currency?.label,
    denominationCode: 0,
    denominationCount: 0,
    denominationValue: 0,
  }));

  const tellerTransactionColumns = [
    { title: "Date", dataIndex: "date", key: "date" },
    { title: "Currency Code", dataIndex: "currencyCode", key: "currencyCode" },
    {
      title: "Transaction Description",
      dataIndex: "transactionDescription",
      key: "transactionDescription",
    },
    {
      title: "Transaction Amount",
      dataIndex: "transactionAmount",
      key: "transactionAmount",
    },
    {
      title: "Transaction Status",
      dataIndex: "transactionStatus",
      key: "transactionStatus",
    },
  ];

  const getTellerCurrencyInfos = (batchStatus) => {
    if (batchStatus === "O" && denominationEntries.length === 0) {
      return currencyCodes.map((code) => ({
        tellerCurrencyInfo: {
          locale: "en",
          currencyCode: code,
          denomCode: 0,
          denomCount: "0",
          denomValue: 0,
          denomName: code,
        },
      }));
    }

    return denominationEntries.map((entry) => ({
      tellerCurrencyInfo: {
        locale: "en",
        currencyCode: entry.currency,
        denomCode: entry.denomination,
        denomCount: String(entry.count),
        denomValue: entry.value,
        denomName: entry.currency,
      },
    }));
  };

  const getTellerShortageOverageInfos = () =>
    summaryEntries.map((entry) => ({
      tellerShortageOverageInfo: {
        locale: "en",
        currencyCode: entry.currencySummary,
        shortage: entry.shortage,
        overage: entry.overage,
        reason: entry.reason,
        batchId: template?.vaultId?.batchId,
      },
    }));

  useEffect(() => {
    getTemplate();
  }, []);

  const getTemplate = () => {
    let body = {
      session,
    };

    const url = `${apiConfig.TellerBatch}/template?officeId=${userData?.officeId}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
        setLoading(false);
      } else {
        const objList = res;
        let tempDenominationOptions = [];
        let tempCurrencyOptions = [];
        let tempBalanceData = [];
        let tempTransactions = [];

        objList?.denomCodes?.forEach((element) => {
          tempDenominationOptions.push({
            label: element,
            value: element,
          });
        });

        objList?.tellerCcyRestrConfData?.forEach(({ currencyCode }) => {
          tempCurrencyOptions.push({
            label: currencyCode,
            value: currencyCode,
          });
        });

        const allowDenomTracking =
          objList?.tellerCcyRestrConfData?.[0]?.tillId?.allowDenomTracking;

        Object.entries(objList?.tillBalances ?? {}).forEach(
          ([currencyCode, balance]) => {
            tempBalanceData.push({
              key: currencyCode,
              currency: currencyCode,
              balance: BOM.FormatAmount(balance),
            });
          },
        );

        objList?.tellerBatchTransactions?.forEach((element, idx) => {
          tempTransactions.push({
            key: idx,
            date: dayjs(element?.transactionDate).format("DD MMM YYYY") ?? "-",
            currencyCode: element?.currencyCode ?? "-",
            transactionDescription:
              element?.txnType === "REQ"
                ? "Request Cash From Vault"
                : element?.txnType === "SND"
                  ? "Send Cash to Vault"
                  : element?.txnType === "WTH"
                    ? "Teller Cash Withdrawal"
                    : element?.txnType === "DEP"
                      ? "Teller Cash Deposit"
                      : element?.txnType === "CLO"
                        ? "Teller Batch Closure"
                        : "-",
            transactionAmount: element?.amount?.toLocaleString() ?? "-",
            currency: element?.currencyCode ?? "-",
            transactionStatus:
              element?.status === 2 ? "Received" : "Unprocessed",
          });
        });

        setTemplate(objList);
        setTransactionsData(tempTransactions);
        setCurrencyDenominationOptions(tempDenominationOptions);
        setCurrencyOptions(tempCurrencyOptions);
        setAllowDenomTracking(allowDenomTracking);
        setCurrencyCodes(objList.currencyCodes);
        setBalanceData(tempBalanceData);
        setBatchUser(objList?.vaultUser?.id);
        setTellerStatus(objList?.vaultId?.batchStatus ?? "C");

        form.setFieldsValue({
          branchCode:
            objList?.branchCode?.externalId ?? objList?.branchCode?.id,
          batchStatus: objList?.vaultId?.batchStatus,
          date:
            objList?.vaultId?.batchStatus === "O" &&
            objList?.vaultId?.businesDate
              ? dayjs(objList.vaultId?.businesDate)
              : null,
        });
        setLoading(false);
      }
    });
  };

  const onSubmit = (values) => {
    const isReopeningClosedBatchToday =
      values?.batchStatus === "O" &&
      getBatchStatusCode(template?.vaultId?.batchStatus) === "C" &&
      isSameAsToday(
        template?.vaultId?.businesDate ?? template?.vaultId?.businessDate,
      );

    const payload = {
      locale: "en",
      dateFormat: "dd MMMM yyyy",
      businesDate: BOM.DateTime(values.date, "only-date"),
      branchCode: template?.branchCode?.id,
      batchStatus: values?.batchStatus,
      batchUser: batchUser,
      vaultId: template?.vaultId?.batchId,
      tellerCurrencyInfos: getTellerCurrencyInfos(values?.batchStatus),
      tellerShortageOverageInfos: getTellerShortageOverageInfos(),
      allowDenomtracking: false,
      method:
        values?.batchStatus === "O" && !isReopeningClosedBatchToday
          ? "POST"
          : "PUT",
      session,
    };

    const url = `${apiConfig.TellerBatch}?officeId=${userData?.officeId}`;

    setLoading(true);
    BOM.FetchReqAction(
      payload,
      url,
      (err, res) => {
        if (err) {
          BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
          setLoading(false);
        } else {
          BOM.AlertMsg(cnt.SUCCESS, "Teller batch updated successfully");
          setDenominationEntries([]);
          getTemplate();
          setLoading(false);
          form.resetFields();
        }
      }
    );
  };

  const hasDenominationEntries = () => {
    if (denominationEntries.length > 0) return true;

    BOM.AlertMsg(
      cnt.WARNING,
      "Please add at least one currency denomination entry",
    );
    return false;
  };

  const handleOpenTill = () => {
    let label = "Do you really want to open teller batch?";

    Notiflix.Confirm.show("Are you sure?", label, "Yes", "No", () => {
      onSubmit({
        ...form.getFieldsValue(),
        batchStatus: "O",
      });
    });
  };

  const handleCloseTill = () => {
    if (!hasDenominationEntries()) return;

    let label = "Do you really want to close teller batch?";

    Notiflix.Confirm.show("Are you sure?", label, "Yes", "No", () => {
      onSubmit({
        ...form.getFieldsValue(),
        batchStatus: "C",
      });
    });
  };

  return (
    <MainLayout
      isEnableRightSidebar={false}
      pageItems={staticConfig.tellerBatchItems}
    >
      <TopContent
        pageTitle={labelConfig.manageTellerBatch}
        //  createPageTitle={labelConfig.createMoneyMarketBond}
      />
      <Card
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
          marginTop: 20,
        }}
      >
        <Spin spinning={loading}>
          <Form form={form} layout="vertical" onFinish={onSubmit}>
            <Row justify="end" style={{ marginBottom: 12 }}>
              <Button
                className="of-primaryBtnColor"
                style={{
                  width: "10%",
                }}
                icon={<UnorderedListOutlined />}
                onClick={handleOpenTill}
                disabled={tellerStatus === "O"}
              >
                Open Till
              </Button>
              <Button
                className="of-primaryBtnColor"
                style={{
                  width: "10%",
                  marginLeft: 10,
                }}
                shape="default"
                icon={<CloseSquareOutlined />}
                // htmlType="submit"
                onClick={handleCloseTill}
                disabled={tellerStatus === "C"}
              >
                Close Till
              </Button>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="date"
                  label={<Text strong>Date</Text>}
                  //   rules={[
                  //     {
                  //       required: true,
                  //       message: "Required Field",
                  //     },
                  //   ]}
                >
                  <DatePicker style={{ width: "100%", height: 44 }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="branchCode"
                  label={<Text strong>Branch Code</Text>}
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: "Required Field",
                  //   },
                  // ]}
                >
                  <Input
                    disabled
                    className="of-bold-disable-input"
                    value={"1"}
                    style={{ width: "100%", height: 44 }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Divider />

            {tellerStatus === "C" && (
              <>
                <Title level={5}>Currency Denomination Info</Title>
                <Table
                  style={{ marginTop: 16, marginBottom: 24 }}
                  dataSource={denominationData}
                  columns={denominationColumns}
                  pagination={false}
                  size="small"
                />
              </>
            )}

            {tellerStatus === "O" && (
              <>
                <Title level={5}>Balance(s)</Title>
                <Table
                  dataSource={balanceData}
                  columns={balanceColumns}
                  pagination={false}
                  size="small"
                />

                <CurrencyDenominationInfo
                  form={form}
                  currencyOptions={currencyOptions}
                  denominationOptions={currencyDenominationOptions}
                  denominationEntries={denominationEntries}
                  setDenominationEntries={setDenominationEntries}
                  allowDenomTracking={allowDenomTracking}
                />
                {denominationEntries.length > 0 && (
                  <CurrencySummary
                    form={form}
                    currencyOptions={currencyOptions}
                    summaryEntries={summaryEntries}
                    setSummaryEntries={setSummaryEntries}
                  />
                )}

                <Divider />
                <Title level={5}>Teller Transaction(s)</Title>
                <Table
                  style={{ marginBottom: 24 }}
                  dataSource={transactionsData}
                  columns={tellerTransactionColumns}
                  // pagination={false}
                  pagination={{
                    position: ["bottomCenter"],
                    pageSize: 10,
                  }}
                  size="small"
                />
              </>
            )}
          </Form>
        </Spin>
      </Card>
    </MainLayout>
  );
}

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = { rightAction };
export default connect(mapStateToProps, mapDispatchToProps)(TellerBatch);
